import { useMemo, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router";
import { ArrowDown, ArrowRight, Info, Search } from "lucide-react";

import { EstadoDeError } from "@/components/estados/EstadoDeError";
import { EstadoSinConexion } from "@/components/estados/EstadoSinConexion";
import { CampoDeRango } from "@/components/referencias/CampoDeRango";
import { RotuloDeFiltro } from "@/components/RotuloDeFiltro";
import { Button } from "@/components/ui/button";
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useCatalogosDeInformesParaReferencias,
  useInformesParaReferencias,
} from "@/hooks/useReferencias";
import { cn } from "@/lib/utils";
import { useAccionDemorada } from "@/hooks/useAccionDemorada";
import { estadoDeConsulta } from "@/lib/estadoDeConsulta";
import { formatearDecimal } from "@/components/informes/formato";
import {
  comunasDelInforme,
  describirInforme,
  ETIQUETA_CORTA_ESTADO,
  ETIQUETA_ESTADO,
  type EstadoConTasacion,
  type FiltrosDeInformesParaReferencias,
  type InformeParaReferencias,
} from "@/lib/referencias";

/** Filas de esqueleto mientras llega el primer lote. */
const FILAS_SKELETON = 4;

/**
 * Cuántos informes trae cada lote, como el diseño.
 *
 * Vive acá y no se lee de la respuesta: es lo que la pantalla PIDE, y pedirlo
 * en función de lo que el servidor contestó la vez anterior haría que el
 * tamaño del lote dependiera de la respuesta que se está por reemplazar.
 */
const LOTE = 6;

/** Ventanas del filtro de última actualización. `0` es el histórico. */
const VENTANAS: { valor: number; etiqueta: string }[] = [
  { valor: 30, etiqueta: "Últimos 30 días" },
  { valor: 90, etiqueta: "Últimos 90 días" },
  { valor: 365, etiqueta: "Últimos 12 meses" },
  { valor: 0, etiqueta: "Histórico" },
];

const ORDENES: { valor: NonNullable<FiltrosDeInformesParaReferencias["orden"]>; etiqueta: string }[] = [
  { valor: "actualizado", etiqueta: "Actualizado primero" },
  { valor: "sin_referencias", etiqueta: "Sin referencias primero" },
  { valor: "superficie", etiqueta: "Superficie mayor" },
  { valor: "cliente", etiqueta: "Cliente A-Z" },
];

const ESTADOS: EstadoConTasacion[] = [
  "en_proceso",
  "en_espera_revision_final",
  "finalizado",
];

/**
 * Paso 1 del módulo: elegir a qué informe se le enlazan referencias de mercado.
 *
 * LOS FILTROS VIVEN EN LA URL y no en `useState`, como en el resto de los
 * listados: recargar, compartir el enlace y el botón atrás conservan la
 * elección. Lo único que NO va en la url es el informe seleccionado —es una
 * elección de un momento, no una vista que alguien quiera compartir.
 *
 * PAGINA ACUMULANDO, no por números. El diseño pide "Cargar N más" porque acá
 * el usuario está buscando UN informe entre varios: paginar lo obligaría a
 * recordar en qué página vio cuál.
 */
/**
 * Cómo se ve un selector con un filtro PUESTO: borde verde, tinta plena y un
 * peso más.
 *
 * El diseño lo marca y vale la pena: son cuatro selectores en fila y, todos
 * iguales, hay que leer sus valores uno por uno para saber si queda alguno
 * filtrando. Con la marca se ve de un vistazo.
 *
 * El peso va CON el modificador `data-[size=compacto]:`, no suelto: el propio
 * `NativeSelect` fija ahí su 600, y tailwind-merge no deja que una clase sin
 * modificador pise a una que lo tiene. Suelto, no pasaba nada.
 */
function marcaDeFiltroPuesto(puesto: boolean): string {
  return puesto ? "border-primary/35 text-ink data-[size=compacto]:font-bold" : "";
}

export function ElegirInformeParaReferencias() {
  const [params, setParams] = useSearchParams();
  const [seleccionado, setSeleccionado] = useState<number | null>(null);

  // Cuántos LOTES se pidieron. Vive acá y no en la url: es el estado de una
  // sesión de búsqueda, y compartir un enlace con "cargá cuatro lotes" no le
  // sirve a nadie.
  const [lotes, setLotes] = useState(1);

  const filtros: FiltrosDeInformesParaReferencias = useMemo(
    () => ({
      buscar: params.get("buscar") ?? undefined,
      estado: (params.get("estado") as EstadoConTasacion | null) ?? undefined,
      comunaCodigo: params.get("comuna") ?? undefined,
      cliente: params.get("cliente") ?? undefined,
      ejecutivoId: params.has("ejecutivo") ? Number(params.get("ejecutivo")) : undefined,
      actualizacion: params.has("dias") ? Number(params.get("dias")) : 90,
      superficieMin: params.has("sup_min") ? Number(params.get("sup_min")) : undefined,
      superficieMax: params.has("sup_max") ? Number(params.get("sup_max")) : undefined,
      sinReferencias: params.get("sin_referencias") === "1",
      orden: (params.get("orden") as FiltrosDeInformesParaReferencias["orden"]) ?? "actualizado",
    }),
    [params],
  );

  // EL LOTE VIAJA AL SERVIDOR, y ahí estaba el defecto: esto pedía siempre
  // `pagina: 1`, o sea seis informes, y después recortaba `slice(0, lotes * 6)`
  // sobre un arreglo de seis. Apretar "Cargar más" subía `lotes` y no había
  // nada más que recortar: el botón no hacía absolutamente nada.
  //
  // Se pide un lote MÁS GRANDE en vez de la página siguiente, igual que el
  // catálogo del paso 2: así no hay que concatenar páginas ni resolver qué pasa
  // cuando el orden cambia entre una y la siguiente.
  const consulta = useInformesParaReferencias({ ...filtros, porPagina: LOTE * lotes });
  /**
   * `refrescando` es el que faltaba: hay resultado EN PANTALLA y una petición
   * en vuelo. El listado conserva lo anterior mientras trae lo nuevo
   * (`placeholderData`) —sin eso cada "cargar más" vaciaría la tabla que la
   * persona está leyendo— así que ni `cargando` ni el vacío se encienden, y
   * mover un filtro dejaba el conteo y las filas ANTERIORES como si fueran la
   * respuesta.
   *
   * El aviso que sale de acá es más DÉBIL que el del paso 2, y a propósito.
   * Allá hay dos consultas y la de los puntos excluye la paginación de su
   * clave, así que se puede afirmar "esto es del filtro anterior". Acá hay una
   * sola: un filtro nuevo y un "cargar más" se ven idénticos desde afuera, y lo
   * único cierto en los dos casos es que la lista todavía no está.
   */
  const { hayError, sinConexion, cargando, recargando, refrescando } =
    estadoDeConsulta(consulta);

  // LOS CONTROLES DEL FILTRO SON UNA CONSULTA APARTE, y su fracaso NO tumba la
  // pantalla: sin ella los chips pierden su número, los selectores quedan en
  // "Todas/Todos" y el slider se apaga, pero la tabla —que es a lo que se
  // vino— sigue funcionando. Por eso se lee con `?.` en todos lados y nunca
  // se mira su `isError`.
  const catalogos = useCatalogosDeInformesParaReferencias().data;

  const pagina = consulta.data;
  // Lo que llega YA es el lote pedido: no se recorta acá. El `slice` que había
  // era justamente lo que hacía parecer que la paginación funcionaba.
  const visibles = pagina?.informes ?? [];
  const total = pagina?.total ?? 0;
  const quedan = Math.max(total - visibles.length, 0);

  const elegido = visibles.find((informe) => informe.id === seleccionado) ?? null;

  /**
   * Cambia un filtro y vuelve al primer lote.
   *
   * Sin el reseteo, un filtro nuevo se leería sobre el acumulado anterior y la
   * cuenta del pie diría cualquier cosa.
   */
  function cambiarFiltro(clave: string, valor: string | null) {
    const siguiente = new URLSearchParams(params);

    if (valor === null || valor === "") {
      siguiente.delete(clave);
    } else {
      siguiente.set(clave, valor);
    }

    setParams(siguiente, { replace: true });
    setLotes(1);
    // La selección se limpia: el informe elegido puede haber quedado fuera del
    // filtro, y dejar el panel derecho mostrando algo que ya no está en la
    // tabla es peor que pedirlo de nuevo.
    setSeleccionado(null);
  }

  /**
   * El buscador, que es el ÚNICO filtro de esta pantalla que se teclea.
   *
   * Los demás son selects, chips e interruptores: un cambio, una consulta, y
   * está bien. Escribir no: `filtros` sale de la url y la url es parte de la
   * `queryKey` del listado, así que teclear "Los Maitenes" mandaba DOCE
   * peticiones —cada una con su preflight— y encima tiraba los lotes cargados y
   * la selección del panel derecho en cada letra.
   *
   * Es el mismo defecto del slider con otra forma. Allá el gesto tiene final
   * —soltar la manija— y se usa `onValueCommit`; acá nadie avisa que terminó de
   * escribir, así que se espera a que la mano se detenga.
   */
  const buscar = useAccionDemorada((texto: string) => cambiarFiltro("buscar", texto));

  function limpiarFiltros() {
    setParams(new URLSearchParams(), { replace: true });
    setLotes(1);
    setSeleccionado(null);
  }

  /**
   * Cambia el rango de superficie, escribiendo los DOS extremos de una vez.
   *
   * Con dos llamadas seguidas a `cambiarFiltro`, cada una parte de la misma
   * url del render anterior y la segunda pisa a la primera: el mínimo se
   * perdía en silencio. Es la misma trampa que ya documenta el paso 2.
   */
  function cambiarSuperficie(min: number, max: number) {
    const siguiente = new URLSearchParams(params);
    siguiente.set("sup_min", String(min));
    siguiente.set("sup_max", String(max));
    setParams(siguiente, { replace: true });
    setLotes(1);
    setSeleccionado(null);
  }

  const hayFiltros = [
    "buscar",
    "estado",
    "comuna",
    "cliente",
    "ejecutivo",
    "dias",
    "sup_min",
    "sup_max",
    "sin_referencias",
    "orden",
  ].some((clave) => params.has(clave));

  // El techo del slider sale de los datos: con un 100 quemado —el rango del
  // prototipo— el predio de 796 ha no se podría alcanzar nunca.
  const techoSuperficie = catalogos?.superficie_maxima ?? null;
  const rangoSuperficie: [number, number] = [
    filtros.superficieMin ?? 0,
    filtros.superficieMax ?? (techoSuperficie ?? 0),
  ];

  return (
    /* El padding de la pantalla es `p-6 lg:p-8` y NO uno propio: es el que usa
       `TopBar` en horizontal (`px-6 lg:px-8`), así que el cuerpo arranca en la
       misma vertical que el título en TODOS los anchos. Sin él, el contenido
       quedaba a ras del borde del panel y el título flotaba solo, sangrado. */
    <div className="flex flex-col gap-5 px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
      {/* La bajada a la izquierda y el paso a paso a la derecha, en UNA fila:
          es la misma disposición del `<header>` del diseño. Ahí ese bloque
          incluye además la miga y el título; acá esos dos viven en el `TopBar`,
          7 px más arriba, así que la banda se lee igual. */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        {/* `text-caption` (11,25) y no `text-body` (14,1): en el diseño esta
            bajada mide 12 px con peso 600, que es exactamente Caption. Es
            texto de APOYO del título, no cuerpo del documento. */}
        <p className="text-ink-muted text-caption max-w-[52rem]">
          Las referencias de mercado se enlazan a un informe. Busca el informe, confirma
          el predio y continúa a seleccionar transacciones y ofertas.
        </p>

        <PasosDelEnlace />
      </div>

      {/* --- Filtros --------------------------------------------------

          `bg-superficie-sutil` y no `bg-card`: el panel tiene que LEERSE como
          una franja de contexto contra el blanco del contenido. En blanco puro
          —como estaba— se confunde con el fondo y los controles quedan
          flotando sin caja. El token ya existía sin estrenar; lo único que hubo
          que agregarle fue su valor en modo oscuro. */}
      <section className="border-border bg-superficie-sutil flex flex-col gap-3 rounded-xl border-[1.5px] px-3.5 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[18rem] flex-1">
            <Search
              className="text-ink-hint pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
              aria-hidden
            />
            <Input
              id="buscar-informe"
              aria-label="Buscar informe"
              className="pl-9 font-semibold"
              placeholder="Buscar por predio, cliente, rol SII o número de informe"
              defaultValue={params.get("buscar") ?? ""}
              onChange={(evento) => buscar(evento.target.value)}
            />
          </div>

          {/* Chips de estado: multi en el diseño, uno por vez acá — el backend
              valida un solo estado y ofrecer varios sería prometer un filtro
              que el endpoint no hace. */}
          <div className="flex flex-wrap items-center gap-1.5">
            {ESTADOS.map((estado) => (
              <ChipDeEstado
                key={estado}
                estado={estado}
                activo={params.get("estado") === estado}
                cuantos={catalogos?.conteo_por_estado?.[estado] ?? null}
                onElegir={() =>
                  cambiarFiltro("estado", params.get("estado") === estado ? null : estado)
                }
              />
            ))}
          </div>

          {/* SIEMPRE VISIBLE, deshabilitado cuando no hay nada que limpiar.
              Apareciendo y desapareciendo movía los chips de lugar justo
              después de apretar uno, que es cuando el ojo todavía está ahí.
              Deshabilitado y no oculto porque el hueco es el punto; y
              deshabilitado y no siempre activo porque limpiar nada es una
              acción que no hace nada. */}
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="ml-auto"
            disabled={!hayFiltros}
            onClick={limpiarFiltros}
          >
            Limpiar filtros
          </Button>
        </div>

        {/* La segunda fila, separada por un divisor: arriba se BUSCA (texto y
            estado), abajo se ACOTA (dónde, de quién, cuándo, cuán grande). El
            divisor es la única línea del panel y por eso va suave (`divisor`,
            no `border`): separa dos grupos del mismo bloque, no dos bloques. */}
        <div className="border-divisor flex flex-wrap items-end gap-4 border-t pt-2.5">
          <CampoDeFiltro etiqueta="Comuna" id="filtro-comuna" ancho="w-[9.5rem]">
            <NativeSelect
              size="compacto"
              id="filtro-comuna"
              className={marcaDeFiltroPuesto(params.has("comuna"))}
              value={params.get("comuna") ?? ""}
              onChange={(evento) => cambiarFiltro("comuna", evento.target.value)}
            >
              <option value="">Todas</option>
              {(catalogos?.comunas ?? []).map((comuna) => (
                <option key={comuna.codigo} value={comuna.codigo}>
                  {comuna.nombre}
                </option>
              ))}
            </NativeSelect>
          </CampoDeFiltro>

          <CampoDeFiltro etiqueta="Cliente" id="filtro-cliente" ancho="w-[13rem]">
            <NativeSelect
              size="compacto"
              id="filtro-cliente"
              className={marcaDeFiltroPuesto(params.has("cliente"))}
              value={params.get("cliente") ?? ""}
              onChange={(evento) => cambiarFiltro("cliente", evento.target.value)}
            >
              <option value="">Todos</option>
              {(catalogos?.clientes ?? []).map((cliente) => (
                <option key={cliente} value={cliente}>
                  {cliente}
                </option>
              ))}
            </NativeSelect>
          </CampoDeFiltro>

          <CampoDeFiltro etiqueta="Ejecutivo" id="filtro-ejecutivo" ancho="w-[10.5rem]">
            <NativeSelect
              size="compacto"
              id="filtro-ejecutivo"
              className={marcaDeFiltroPuesto(params.has("ejecutivo"))}
              value={params.get("ejecutivo") ?? ""}
              onChange={(evento) => cambiarFiltro("ejecutivo", evento.target.value)}
            >
              <option value="">Todos</option>
              {(catalogos?.ejecutivos ?? []).map((ejecutivo) => (
                <option key={ejecutivo.id} value={String(ejecutivo.id)}>
                  {ejecutivo.nombre}
                </option>
              ))}
            </NativeSelect>
          </CampoDeFiltro>

          <CampoDeFiltro
            etiqueta="Última actualización"
            id="filtro-dias"
            ancho="w-[11rem]"
          >
            <NativeSelect
              size="compacto"
              id="filtro-dias"
              className={marcaDeFiltroPuesto(params.has("dias"))}
              value={String(filtros.actualizacion ?? 90)}
              onChange={(evento) => cambiarFiltro("dias", evento.target.value)}
            >
              {VENTANAS.map((ventana) => (
                <option key={ventana.valor} value={ventana.valor}>
                  {ventana.etiqueta}
                </option>
              ))}
            </NativeSelect>
          </CampoDeFiltro>

          {/* El techo sale del alcance, no de una constante: ver
              `techoSuperficie`. Sin ningún informe con superficie declarada no
              hay escala que dibujar, y el control se apaga diciendo por qué en
              vez de ofrecer un rango de 0 a 0. */}
          <CampoDeRango
            className="w-[13rem]"
            rotulo="Superficie del predio"
            valor={rangoSuperficie}
            onCambio={([min, max]) => cambiarSuperficie(min, max)}
            min={0}
            max={techoSuperficie ?? 1}
            leerValor={([min, max]) => `${min} – ${max} ha`}
            extremoMin="0"
            extremoMax={techoSuperficie === null ? "—" : `${techoSuperficie} ha`}
            deshabilitado={techoSuperficie === null}
            motivo="Ningún informe tiene la superficie declarada."
          />

        </div>

        {/* TERCERA FILA, y propia: el switch estaba al final de la segunda, así
            que caía abajo solo cuando el ancho lo obligaba a envolverse — con la
            ventana un poco más grande se metía al lado del slider y la fila
            quedaba con seis controles apretados.

            Acá el hueco de la izquierda es parte de la lectura: el switch no
            acota QUÉ columna se mira como los de arriba, sino que saca informes
            del listado. El blanco a su derecha es lo que dice que es otra cosa. */}
        <div className="flex items-center gap-2">
          <Switch
            id="filtro-sin-referencias"
            checked={filtros.sinReferencias ?? false}
            onCheckedChange={(marcado) =>
              cambiarFiltro("sin_referencias", marcado ? "1" : null)
            }
          />
          <label htmlFor="filtro-sin-referencias" className="text-ink-muted text-xs font-bold">
            Solo sin referencias enlazadas
          </label>
        </div>
      </section>

      {/* --- Tabla y panel -------------------------------------------- */}
      {hayError ? (
        <EstadoDeError
          titulo="No se pudieron cargar los informes"
          error={consulta.error}
          onReintentar={() => void consulta.refetch()}
          reintentando={recargando}
        />
      ) : sinConexion ? (
        <EstadoSinConexion que="los informes" />
      ) : (
        /* `lg:items-start` para que el panel derecho no se estire al alto de la
           tabla: es lo que le permite quedarse pegado al scrollear. */
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start">
          <div className="flex flex-col gap-3">
            {/* El orden vive ACÁ y no entre los filtros, como el diseño: los
                filtros deciden QUÉ informes se ven y el orden en qué SECUENCIA
                — es una propiedad de la lista, no un recorte de ella, y por eso
                va sobre la lista y alineado a su derecha. */}
            <div className="flex flex-wrap items-center gap-3">
              {/* DOS PIEZAS Y NO UNA FRASE CORRIDA, como el diseño, y es lo
                  único de esta pantalla que estaba más CHICO de la cuenta.

                  Cuántos informes hay es el encabezado de la lista: va a 15 px
                  (Body), que es el tamaño que le da el diseño. De cuántos
                  salieron es la nota al pie de ese número, y va chica y en gris.

                  Estaban las dos juntas a 11,25 px, así que el número que
                  encabeza la tabla pesaba lo mismo que su aclaración. */}
              {/* EL CONTEO SE REEMPLAZA MIENTRAS VIAJA LA CONSULTA, no se deja
                  atenuado: es el dato más engañoso de los que sobreviven al
                  cambio de filtro, porque un número nítido encabezando la tabla
                  se lee como el resultado de lo que se acaba de tocar. */}
              {refrescando ? (
                <p
                  role="status"
                  className="text-ink-muted flex items-center gap-2 text-sm font-bold"
                >
                  <Spinner className="size-4 flex-none" aria-hidden />
                  Buscando informes…
                </p>
              ) : (
                <div className="flex flex-wrap items-baseline gap-2">
                  <p className="text-h4 font-bold">
                    {total} {total === 1 ? "informe" : "informes"}
                  </p>
                  <p className="text-ink-hint font-mono text-[0.6875rem] font-bold">
                    de {pagina?.totalGeneral ?? 0} en el sistema
                  </p>
                </div>
              )}

              <div className="ml-auto w-[12.5rem]">
                <NativeSelect
                  size="compacto"
                  id="filtro-orden"
                  aria-label="Ordenar por"
                  className={marcaDeFiltroPuesto(params.has("orden"))}
                  value={filtros.orden ?? "actualizado"}
                  onChange={(evento) => cambiarFiltro("orden", evento.target.value)}
                >
                  {ORDENES.map((orden) => (
                    <option key={orden.valor} value={orden.valor}>
                      {orden.etiqueta}
                    </option>
                  ))}
                </NativeSelect>
              </div>
            </div>

            {/* `aria-busy` y el atenuado son la misma afirmación por dos vías:
                el rótulo de arriba lo lee quien mira ahí, y esto marca la tabla
                para el resto —incluido un lector de pantalla, que si no cantaría
                como resultado unas filas que ya no corresponden al filtro.

                Va sobre la TABLA y no sobre la columna entera: el selector de
                orden queda afuera, nítido y usable, porque es un control y no
                un resultado. NO se bloquean los clics: elegir un informe de los
                que se ven sigue siendo válido, y una pantalla que deja de
                responder se lee como colgada. */}
            <section
              aria-label="Informes del filtro"
              aria-busy={refrescando}
              className={cn(
                "bg-card overflow-hidden rounded-xl border-[1.5px] transition-opacity",
                refrescando && "opacity-60",
              )}
            >
              <Table>
                {/* El encabezado va sobre la MISMA franja tenue que el panel de
                    filtros, como el diseño: es lo que lo separa de las filas
                    sin necesidad de subirle el peso a la letra, que competiría
                    con el dato. */}
                <TableHeader className="bg-superficie-sutil [&_tr]:border-divisor [&_tr]:border-b-[1.5px]">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-10" />
                    {/* En VERSALITAS y con la letra chica, como el diseño: en una
                        tabla de seis columnas el encabezado tiene que pesar menos
                        que el dato, o compite con él. La receta vive en el
                        `TableHead` del DS —acá estaba como constante local y se
                        había vuelto a escribir en otras dos pantallas, con dos
                        tamaños y dos colores distintos para lo mismo. */}
                    <TableHead variant="versalitas">Predio / informe</TableHead>
                    <TableHead variant="versalitas">Cliente / ejecutivo</TableHead>
                    <TableHead variant="versalitas">Comuna</TableHead>
                    <TableHead variant="versalitas" className="text-right">
                      Sup. ha
                    </TableHead>
                    <TableHead variant="versalitas">Estado</TableHead>
                    <TableHead variant="versalitas" className="text-right">
                      Refs.
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cargando
                    ? Array.from({ length: FILAS_SKELETON }).map((_, indice) => (
                        <TableRow key={indice}>
                          <TableCell colSpan={7}>
                            <Skeleton className="h-8 w-full" aria-label="Cargando informe" />
                          </TableCell>
                        </TableRow>
                      ))
                    : visibles.map((informe) => (
                        <FilaDeInforme
                          key={informe.id}
                          informe={informe}
                          elegido={informe.id === seleccionado}
                          onElegir={() => setSeleccionado(informe.id)}
                        />
                      ))}
                </TableBody>
              </Table>

              {!cargando && visibles.length === 0 && (
                <Empty>
                  <EmptyHeader>
                    {/* El `Empty` del DS titula en 500 y el diseño pide 700
                        para este estado. Se ajusta acá y no en el componente:
                        lo comparten seis pantallas cuyo diseño no se midió. */}
                    <EmptyTitle className="text-[0.84375rem] font-bold">
                      Ningún informe con estos filtros
                    </EmptyTitle>
                    <EmptyDescription className="font-semibold">
                      Prueba con otro término de búsqueda o amplía la ventana de
                      actualización.
                    </EmptyDescription>
                  </EmptyHeader>
                </Empty>
              )}
            </section>

            {quedan > 0 && (
              <div className="flex flex-col items-center gap-1">
                {/* Sin el estado ocupado el botón se queda igual medio segundo
                    y se aprieta de nuevo, pidiendo un lote más del que nadie
                    quería. El ícono se REEMPLAZA por la rueda en vez de sumarla:
                    la flecha dice "hay más abajo", que es lo mismo que la rueda
                    está diciendo mientras trabaja. */}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="font-extrabold"
                  onClick={() => setLotes((actual) => actual + 1)}
                  disabled={recargando}
                >
                  {recargando ? (
                    <Spinner className="size-4" aria-hidden />
                  ) : (
                    <ArrowDown className="size-4" />
                  )}
                  Cargar {Math.min(quedan, LOTE)} más
                </Button>
                <p className="text-ink-hint text-caption font-mono font-bold">
                  {visibles.length} de {total} · quedan {quedan}
                </p>
              </div>
            )}
          </div>

          <PanelDelInforme informe={elegido} />
        </div>
      )}
    </div>
  );
}

/**
 * En qué paso del enlace estás: elegir el informe (1) y seleccionar las
 * referencias (2).
 *
 * DOS PASOS Y NO UNA BARRA DE PROGRESO, como el diseño: son dos pantallas
 * nombradas, no un porcentaje. Lo que aporta es decir que ELEGIR EL INFORME NO
 * ES EL TRABAJO —es el trámite previo— y que después viene lo otro. Sin eso,
 * quien abre esta pantalla desde el menú puede creer que se equivocó de lugar.
 *
 * EL PASO 2 NO NAVEGA, y no es un olvido: su url exige un informe
 * (`/referencias/:id`) y todavía no hay ninguno elegido. Un enlace muerto ahí
 * sería peor que un rótulo apagado, que al menos dice que falta algo.
 *
 * Va como LISTA ORDENADA y no como navegación: no hay a dónde ir, y el orden
 * es justamente lo que se está comunicando.
 */
function PasosDelEnlace() {
  return (
    <ol className="flex flex-none items-center gap-2">
      <PasoDelEnlace numero={1} rotulo="Elegir informe" actual />
      <PasoDelEnlace numero={2} rotulo="Seleccionar referencias" />
    </ol>
  );
}

function PasoDelEnlace({
  numero,
  rotulo,
  actual = false,
}: {
  numero: number;
  rotulo: string;
  actual?: boolean;
}) {
  return (
    <li
      className={cn(
        "flex items-center gap-2 rounded-[0.6875rem] border-[1.5px] px-3 py-2",
        actual ? "border-primary/35 bg-accent" : "border-border bg-card",
      )}
    >
      <span
        className={cn(
          "flex size-[1.1875rem] flex-none items-center justify-center rounded-full font-mono text-[0.65625rem] font-extrabold",
          actual ? "bg-primary text-primary-foreground" : "bg-divisor text-ink-hint",
        )}
        aria-hidden
      >
        {numero}
      </span>
      <span
        className={cn(
          "text-xs whitespace-nowrap",
          actual ? "text-ink font-extrabold" : "text-ink-hint font-bold",
        )}
      >
        {/* El número también va en el texto accesible: sin él, un lector de
            pantalla anuncia dos rótulos sueltos y no un orden. */}
        <span className="sr-only">Paso {numero}: </span>
        {rotulo}
        {actual && <span className="sr-only"> (paso actual)</span>}
      </span>
    </li>
  );
}

function FilaDeInforme({
  informe,
  elegido,
  onElegir,
}: {
  informe: InformeParaReferencias;
  elegido: boolean;
  onElegir: () => void;
}) {
  const comunas = comunasDelInforme(informe);

  return (
    <TableRow
      data-state={elegido ? "selected" : undefined}
      className={cn(
        "cursor-pointer",
        // EL VERDE VA CON EL MISMO MODIFICADOR QUE LA CLASE QUE PISA, y sin eso
        // no se veía: `TableRow` trae `data-[state=selected]:bg-muted` —gris— y
        // tailwind-merge NO deja que una clase SIN modificador pise a una que
        // lo tiene. El verde estaba escrito y el gris ganaba siempre.
        //
        // `success-soft` al 45 % sobre blanco da #f6fbf5, que es el #f7fcf6 del
        // diseño; y al ser un token, en modo oscuro acompaña solo.
        elegido && "data-[state=selected]:bg-success-soft/45",
      )}
      onClick={onElegir}
    >
      {/* LA BARRA VERDE VA COMO SOMBRA INTERIOR DE LA PRIMERA CELDA, y las dos
          mitades de esa frase son correcciones de un defecto real.
        
          SOMBRA y no `border-left`: un borde en la fila desplaza todas las
          celdas 3 px al marcarla, y la tabla entera tiembla al elegir.
        
          Y EN LA CELDA, no en el `<tr>` con un `::before`, que es como estaba y
          rompía la fila entera: un pseudo-elemento dentro de un `table-row` se
          envuelve en una CELDA ANÓNIMA, así que la fila seleccionada pasaba a
          tener ocho columnas y todo su contenido se corría una a la derecha —el
          nombre del predio terminaba bajo el encabezado "Cliente". Un `<td>` es
          un contenedor normal y no genera nada. Es además lo que hace el
          diseño. */}
      {/* `pl-4` y no el `p-2` de la celda: el diseño separa el radio 14 px del
          borde de la tabla, contra los 7,5 que da el relleno por defecto. */}
      <TableCell
        className={cn("pl-4", elegido && "shadow-[inset_3px_0_0_0_var(--color-primary)]")}
      >
        {/* EL RADIO SE DIBUJA, no se deja al del navegador.
        
            `accent-color` solo tiñe el punto: el ARO queda como cada navegador
            lo pinte, y en Chrome es un gris tenue que casi no se ve. El diseño
            le pide un borde de 1,5 px bien visible —el mismo `--input` que usan
            los campos— y que se ponga verde al elegir.
        
            El `<input>` sigue existiendo y solo se esconde a la vista: es lo que
            hace que la fila se pueda elegir con el teclado y que un lector de
            pantalla anuncie el predio. El foco se dibuja sobre el aro con
            `peer-focus-visible`, así que no se pierde al ocultarlo. */}
        <label className="flex cursor-pointer items-center">
          <input
            type="radio"
            name="informe"
            className="peer sr-only"
            checked={elegido}
            onChange={onElegir}
            aria-label={describirInforme(informe)}
          />
          <span
            className={cn(
              "flex size-5 flex-none items-center justify-center rounded-full border-[1.5px] transition-colors",
              "peer-focus-visible:border-ring peer-focus-visible:ring-ring/50 peer-focus-visible:ring-[3px]",
              elegido ? "border-primary" : "border-input",
            )}
          >
            <span
              className={cn("size-2 rounded-full transition-colors", elegido && "bg-primary")}
            />
          </span>
        </label>
      </TableCell>
      <TableCell>
        {/* El nombre del predio es el dato por el que se elige la fila, así
            que va por encima de la base de la tabla: 12,5 px en el diseño. */}
        <div className="text-[0.8125rem] font-bold">{describirInforme(informe)}</div>
        {/* Y su segunda línea por debajo (10 px en el diseño). Con la base ya
            en 11,25, `text-caption` medía lo mismo que el dato de al lado y
            dejaba de leerse como secundaria. */}
        <div className="text-ink-hint font-mono text-[0.6875rem] font-bold">
          {informe.numero_informe ?? "sin número"}
          {informe.roles?.[0]?.rol_sii ? ` · rol ${informe.roles[0].rol_sii}` : ""}
        </div>
      </TableCell>
      {/* CADA CELDA DECLARA SU PESO, y no es prolijidad: sin clase de peso el
          navegador pone 400, y en el diseño NINGUNA celda baja de 600 —medido
          también en `Informes.dc.html`, donde van entre 500 y 800—. La tabla se
          veía notoriamente más liviana que el diseño y la causa no estaba
          escrita en ningún lado: estaba ausente. */}
      <TableCell>
        <div className="font-semibold">{informe.cliente_nombre ?? "—"}</div>
        {informe.ejecutivo && (
          <div className="text-ink-hint text-[0.6875rem] font-semibold">
            {informe.ejecutivo.first_name} {informe.ejecutivo.last_name}
          </div>
        )}
      </TableCell>
      <TableCell className="font-semibold">
        {comunas.length > 0 ? comunas.join(" · ") : "—"}
      </TableCell>
      <TableCell className="text-right font-mono font-bold">
        {informe.superficie_ha ? formatearDecimal(informe.superficie_ha) : "—"}
      </TableCell>
      <TableCell>
        <BadgeDeEstado estado={informe.estado} />
      </TableCell>
      <TableCell className="text-right font-mono font-bold">
        {informe.referencias > 0 ? (
          <span className="text-primary">{informe.referencias}</span>
        ) : (
          <span className="text-ink-ghost">—</span>
        )}
      </TableCell>
    </TableRow>
  );
}

/**
 * El resumen del informe elegido, con la acción para continuar.
 *
 * Repite datos que la fila ya muestra a propósito: el usuario confirma acá lo
 * que eligió allá, y una confirmación que obliga a volver a mirar la tabla no
 * confirma nada.
 */
function PanelDelInforme({ informe }: { informe: InformeParaReferencias | null }) {
  const comunas = informe ? comunasDelInforme(informe) : [];

  return (
    /* SIEMPRE VISIBLE, con o sin informe elegido, y PEGAJOSO al scrollear.
    
       Antes aparecía como una tarjeta chica con una frase y, al elegir, se
       convertía en un panel alto: la columna crecía de golpe y la página daba
       un salto justo después del clic. Con la estructura fija, elegir solo
       RELLENA lo que ya estaba dibujado.
    
       Pegajoso porque la acción para continuar vive acá: con la tabla larga se
       iba hacia arriba y había que volver a subir para apretar el botón del
       informe que se acababa de elegir.

       ESO NO OCURRÍA, y el `sticky` no era el culpable: el armazón no tenía
       alto fijo, así que el contenedor contra el que se medía nunca scrolleaba
       y la regla no llegaba a activarse. Está explicado en `MainLayout`. El
       tope de alto acompaña por lo mismo que en `PanelDeSeleccion`: pegado y
       más alto que la pantalla, el botón queda abajo del borde y sin forma de
       alcanzarlo. `100cqh` es el alto de `main`, menos los `top-6` y el mismo
       aire abajo. */
    <aside className="scroll-fino flex flex-col gap-4 lg:sticky lg:top-6 lg:max-h-[calc(100cqh-3rem)] lg:overflow-y-auto">
      {/* NEGRO, no gris: el diseño lo pinta `#17181b` con radio 16 y padding 17.
          Va con `superficie-fija` porque ese token NO se invierte en modo
          oscuro — con `bg-ink` el panel quedaba blanco sobre blanco. */}
      <div className="bg-superficie-fija text-superficie-fija-foreground flex flex-col gap-[0.75rem] rounded-[1rem] p-[1.0625rem]">
        <p className="text-superficie-fija-muted text-[0.625rem] font-extrabold tracking-[0.08em] uppercase">
          Informe seleccionado
        </p>
        <div>
          <p className="text-[1.0625rem] leading-tight font-extrabold tracking-[-0.01em]">
            {informe ? describirInforme(informe) : "Ningún informe elegido"}
          </p>
          <p
            className={cn(
              "mt-[0.125rem] font-mono text-[0.8125rem] font-bold",
              informe ? "text-primary" : "text-superficie-fija-ghost",
            )}
          >
            {informe ? (informe.numero_informe ?? "sin número") : "—"}
          </p>
        </div>

        {/* Los seis renglones se dibujan SIEMPRE, en guion cuando no hay nada
            elegido. Es lo que mantiene quieta la altura del panel, y además
            adelanta qué se va a ver: el usuario sabe que va a poder confirmar
            cliente, comuna y rol antes de elegir. */}
        <dl className="flex flex-col gap-[0.4375rem]">
          <Dato rotulo="Cliente" valor={informe?.cliente_nombre ?? "—"} />
          <Dato rotulo="Comuna" valor={comunas.join(" · ") || "—"} />
          <Dato rotulo="Rol SII" valor={informe?.roles?.[0]?.rol_sii ?? "—"} mono />
          <Dato
            rotulo="Superficie"
            valor={
              informe?.superficie_ha
                ? `${formatearDecimal(informe.superficie_ha)} ${informe.unidad_superficie ?? "ha"}`
                : "—"
            }
            mono
          />
          <Dato rotulo="Uso" valor={informe ? describirUso(informe) : "—"} />
          <Dato
            rotulo="Referencias enlazadas"
            valor={informe ? String(informe.referencias) : "—"}
            mono
          />
        </dl>
      </div>

      {/* Las dos acciones van juntas dentro de una tarjeta, como el diseño:
          sueltas se leen como dos botones sin relación entre sí. */}
      {/* `bg-card` y no `bg-white`: en modo oscuro un blanco fijo dejaba esta
          tarjeta encendida al lado del panel negro de arriba. En claro es el
          mismo color, así que no cambia nada de lo que ya se veía. */}
      <div className="border-border bg-card flex flex-col gap-[0.5rem] rounded-[1rem] border-[1.5px] px-[1rem] pt-[0.875rem] pb-[1rem]">
        {/* DESHABILITADOS y no ocultos cuando no hay informe: el hueco es lo
            que evita el salto, y un botón apagado dice "esto se va a poder"
            mientras que uno ausente no dice nada.
        
            Sin informe se dibuja un `<button>` y no un `<Link>`: `asChild`
            sobre un enlace ignora `disabled` —un `<a>` no lo tiene— y quedaría
            un botón que se ve apagado y navega igual, a `/referencias/undefined`. */}
        {informe ? (
          <Button
            className="h-[2.625rem] w-full rounded-[0.6875rem] text-[0.8125rem] font-extrabold"
            asChild
          >
            <Link to={`/referencias/${informe.id}`}>
              Continuar a referencias
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        ) : (
          <Button
            disabled
            className="h-[2.625rem] w-full rounded-[0.6875rem] text-[0.8125rem] font-extrabold"
          >
            Continuar a referencias
            <ArrowRight className="size-4" />
          </Button>
        )}

        {/* La segunda salida del diseño: mirar el informe sin enlazarle nada.
            Abre en la MISMA pestaña porque es un cambio de tarea, no una
            consulta al pasar. */}
        {informe ? (
          <Button
            variant="outline"
            className="h-[2.375rem] w-full rounded-[0.6875rem] text-[0.8125rem] font-bold"
            asChild
          >
            <Link to={`/informes/${informe.id}`}>Abrir el informe</Link>
          </Button>
        ) : (
          <Button
            variant="outline"
            disabled
            className="h-[2.375rem] w-full rounded-[0.6875rem] text-[0.8125rem] font-bold"
          >
            Abrir el informe
          </Button>
        )}

        {/* La nota SIEMPRE ocupa su lugar, por lo mismo que los renglones de
            arriba: sin informe explica por qué los botones están apagados. */}
        <p className="text-ink-muted flex items-start gap-[0.4375rem] text-[0.71875rem] font-semibold">
          <Info className="mt-[0.125rem] size-[0.8125rem] flex-none" />
          <span>
            {!informe
              ? "Elige un informe de la tabla para continuar."
              : informe.referencias > 0
                ? `Este informe ya tiene ${informe.referencias} ${
                    informe.referencias === 1 ? "referencia" : "referencias"
                  }: las nuevas se suman a las existentes.`
                : "Este informe todavía no tiene referencias enlazadas."}
          </span>
        </p>
      </div>

      {informe && !informe.tiene_coordenada && (
        <p className="text-warning-soft-foreground bg-warning-soft text-caption rounded-lg p-3">
          Este informe no tiene coordenada del predio, así que las referencias no se van a
          poder ordenar por distancia.
        </p>
      )}
    </aside>
  );
}

/**
 * Un filtro de la segunda fila: rótulo ARRIBA del control, en versalitas.
 *
 * No usa `FiltroDeListado` —que pone el rótulo AL LADO— porque acá son cuatro
 * en línea: al lado, cada par rótulo+control ocupa casi el doble de ancho y la
 * fila se parte en dos. Arriba, además, alinea los cuatro controles entre sí,
 * que es lo que deja leerlos como un grupo.
 *
 * El `<label>` va atado por `htmlFor` y no como texto suelto: es lo que hace
 * que un lector de pantalla anuncie "Comuna" al llegar al select, y lo que deja
 * que un test lo busque por su rótulo.
 */
function CampoDeFiltro({
  etiqueta,
  id,
  ancho,
  children,
}: {
  etiqueta: string;
  id: string;
  /** El ancho del control, que es fijo: si no, cada select toma el de su
      opción más larga y la fila baila cada vez que cambia el catálogo. */
  ancho: string;
  children: ReactNode;
}) {
  return (
    <div className={cn("flex flex-col gap-1.5", ancho)}>
      <RotuloDeFiltro htmlFor={id}>{etiqueta}</RotuloDeFiltro>
      {children}
    </div>
  );
}

/**
 * Un chip de estado del filtro, con su punto de color y su conteo.
 *
 * EL PUNTO NO ES ADORNO: es lo que ata el chip a la columna Estado de la
 * tabla, que usa el mismo color. Sin él, el chip "En revisión" y el badge
 * "Revisión" de la fila se leen como dos vocabularios distintos.
 *
 * EL CONTEO EVITA ELEGIR UN FILTRO QUE DEVUELVE CERO, que es la misma razón
 * por la que lo lleva el menú de selección múltiple del paso 2. Va sobre el
 * alcance ENTERO y no sobre lo filtrado: contra lo filtrado repetiría el
 * número que ya se ve arriba de la tabla.
 *
 * `null` cuando el catálogo no cargó: se omite el número en vez de imprimir un
 * cero, que afirmaría que no hay ninguno.
 */
function ChipDeEstado({
  estado,
  activo,
  cuantos,
  onElegir,
}: {
  estado: EstadoConTasacion;
  activo: boolean;
  cuantos: number | null;
  onElegir: () => void;
}) {
  const tono = {
    en_proceso: "bg-info-soft text-info-soft-foreground",
    en_espera_revision_final: "bg-warning-soft text-warning-soft-foreground",
    finalizado: "bg-success-soft text-success-soft-foreground",
  }[estado];

  const punto = {
    en_proceso: "bg-info-dot",
    en_espera_revision_final: "bg-warning-dot",
    finalizado: "bg-success-dot",
  }[estado];

  return (
    <button
      type="button"
      aria-pressed={activo}
      // EL NOMBRE ACCESIBLE SE DECLARA EN PROSA porque el número forma parte
      // de lo que el control dice, y del contenido saldría "En proceso 5",
      // leído como un rótulo raro. Con el rótulo solo, en cambio, quien no ve
      // la pantalla perdería justo el dato que evita elegir un filtro vacío.
      aria-label={
        cuantos === null
          ? ETIQUETA_ESTADO[estado]
          : `${ETIQUETA_ESTADO[estado]}, ${cuantos} ${cuantos === 1 ? "informe" : "informes"}`
      }
      onClick={onElegir}
      className={cn(
        "inline-flex h-9 items-center gap-2 rounded-md border-[1.5px] px-3 text-xs font-bold transition-colors",
        activo
          ? cn("border-transparent", tono)
          : "border-input bg-card text-ink-muted hover:border-ink-ghost",
      )}
    >
      <span
        className={cn(
          "size-[0.4375rem] flex-none rounded-full",
          activo ? punto : "bg-input",
        )}
      />
      {ETIQUETA_ESTADO[estado]}
      {cuantos !== null && (
        <span
          className={cn("font-mono text-[0.625rem]", !activo && "text-ink-ghost")}
        >
          {cuantos}
        </span>
      )}
    </button>
  );
}

function Dato({ rotulo, valor, mono }: { rotulo: string; valor: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-superficie-fija-ghost text-[0.71875rem] font-semibold">{rotulo}</dt>
      <dd className={cn("text-right text-[0.71875rem] font-semibold", mono && "font-mono")}>
        {valor}
      </dd>
    </div>
  );
}

/**
 * El estado del informe, como lo dibuja el diseño: un PUNTO de color y el
 * texto corto.
 *
 * El punto no es adorno: en una columna de seis filas es lo que deja barrer los
 * estados sin leer, y separa "Revisión" de "Aprobado" para quien no distingue
 * ámbar de verde. El texto va corto —"Proceso", no "En proceso"— porque el
 * encabezado de la columna ya dice ESTADO y la preposición solo gasta ancho.
 */
function BadgeDeEstado({ estado }: { estado: EstadoConTasacion }) {
  const tono = {
    en_proceso: "bg-info-soft text-info-soft-foreground",
    en_espera_revision_final: "bg-warning-soft text-warning-soft-foreground",
    finalizado: "bg-success-soft text-success-soft-foreground",
  }[estado];

  const punto = {
    en_proceso: "bg-info-dot",
    en_espera_revision_final: "bg-warning-dot",
    finalizado: "bg-success-dot",
  }[estado];

  return (
    <span
      className={cn(
        "inline-flex items-center gap-[0.375rem] rounded-[0.4375rem] px-[0.5rem] py-[0.1875rem] text-[0.625rem] font-extrabold",
        tono,
      )}
    >
      <span className={cn("size-[0.4375rem] flex-none rounded-full", punto)} />
      {ETIQUETA_CORTA_ESTADO[estado]}
    </span>
  );
}

/**
 * El uso del predio, como una frase.
 *
 * El diseño lo muestra como "Agrícola — frutales": el tipo de tasación (el
 * nombre de la plantilla) y su formato de salida, unidos. Con uno solo se muestra ese; sin ninguno, guion.
 */
function describirUso(informe: InformeParaReferencias): string {
  const partes = [informe.plantilla, informe.formato_salida]
    .filter((v): v is string => Boolean(v))
    .map((v) => v.replace(/_/g, " "));

  if (partes.length === 0) return "—";

  return partes.join(" — ");
}
